'use client'

import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangleIcon,
  ArrowLeftRightIcon,
  Loader2Icon,
  PlugIcon,
  UnplugIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { DestructiveConfirmDialog } from '@/components/dialog/settings/destructive-confirm-dialog'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  PARADIGM_TONE_BADGE,
  ParadigmDeskSheet,
} from '@/features/dashboard/components/account-connection/paradigm-desk-sheet'
import {
  paradigmErrorMessage,
  useParadigmBinding,
  useParadigmDisconnect,
} from '@/hooks/use-paradigm-binding'
import {
  paradigmBindingStateKey,
  paradigmBindingTone,
  paradigmRowActions,
} from '@/lib/paradigm/binding'
import { formatRelativeTime } from '@/lib/relative-time'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { PublicAccount } from '@/types/generated/common/account'

// The Paradigm desk binding for one Deribit or Bybit account, inside the settings account
// row.
//
// Mounted only where `supportsParadigmBinding` holds (the backend refuses every other
// venue outright).

/**
 * Square icon button, matching the account row's own action buttons directly below this
 * one (rename, manage access, delete) — same bare `button`, same size and border, same
 * `Tooltip` carrying the label. Deliberately NOT the `Button` component: these two rows
 * sit inches apart in the same dialog and any difference in height or radius reads as a
 * mistake.
 *
 * No red on the destructive action either, for the same reason — `delete` next door is a
 * plain trash icon. The confirmation dialog is what marks it as destructive.
 *
 * The label is the tooltip AND the accessible name. Dropping the visible text is a layout
 * decision; it is not a reason to ship a control a screen reader announces as nothing.
 */
function IconAction({
  label,
  icon: Icon,
  onClick,
  pending,
}: {
  label: string
  icon: LucideIcon
  onClick: () => void
  pending?: boolean
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn([
            'border-foreground/20 text-foreground/65 hover:bg-foreground/5 hover:text-foreground flex size-5 items-center justify-center border transition-colors',
            pending && 'cursor-wait opacity-70',
          ])}
          disabled={pending}
          onClick={onClick}
        >
          {pending ? (
            <Loader2Icon className="size-3 animate-spin text-current" />
          ) : (
            <Icon className="size-3 text-current" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="whitespace-pre-line">{label}</p>
      </TooltipContent>
    </Tooltip>
  )
}

// The binding state is shown to every member — it is plain account information — while
// the actions are owner-only (see `paradigmRowActions`), the same treatment the Lighter
// fee-consent control gets. Every refusal that CAN still happen (plan capability, bad
// credentials, concurrent role change) surfaces verbatim from the server.
//
// Two management actions, and they are not interchangeable. "Change linked account"
// re-points the nickname — `paradigmLinkAccount` overwrites unconditionally, so there is
// no unlink step in between. "Disconnect" clears the whole binding INCLUDING the desk
// id, so it is destructive and confirmed: reconnecting afterwards means re-entering the
// desk access key and secret.
export function ParadigmBindingRow({ account }: { account: PublicAccount }) {
  const t = useTranslations('accountConnection')
  const tCommon = useTranslations('common')
  // `cancel` is the shared settings-dialog label the other destructive confirms use.
  const tSettings = useTranslations('settings')
  const queryClient = useQueryClient()
  const { linkAccount } = useParadigmBinding()
  const [showSheet, setShowSheet] = useState(false)
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [fixing, setFixing] = useState(false)
  const [fixError, setFixError] = useState('')

  const binding = account.paradigmBinding
  const actions = paradigmRowActions(account)
  const tone = binding ? paradigmBindingTone(binding.state) : undefined
  // The nickname the server already worked out this account's trades belong to. Absent
  // when it couldn't pin one down, and then there is nothing to one-click.
  const suggested = binding?.suggestedAccountName

  const invalidateAccounts = () => queryClient.invalidateQueries({ queryKey: ['accounts'] })

  // Invalidates the accounts query itself on success, which is what drops the row back
  // to "not connected" without a reload.
  const { mutateAsync: disconnectParadigm, isPending: isDisconnecting } = useParadigmDisconnect({
    onSuccess: () => {
      toast.success(t('paradigmDisconnected'))
      setShowDisconnectDialog(false)
    },
    onError: (error) => {
      toast.error(error.message || t('paradigmDisconnectFailed'))
    },
  })

  const handleAutoFix = async () => {
    if (!suggested || fixing) return
    setFixing(true)
    setFixError('')
    try {
      await linkAccount(account.accountId, suggested)
      await invalidateAccounts()
      toast.success(t('paradigmMismatchFixed', { name: suggested }))
    } catch (e) {
      const message = paradigmErrorMessage(e, t('paradigmLinkFailed'))
      setFixError(message)
      toast.error(message)
    } finally {
      setFixing(false)
    }
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-foreground/60 text-xs">{t('paradigmRowLabel')}:</span>
          {binding && tone ? (
            <>
              <span className="text-foreground/80 truncate text-xs font-medium">
                {binding.accountName}
              </span>
              <span
                className={cn(
                  'shrink-0 border px-2 py-0.5 text-[10px] font-medium',
                  PARADIGM_TONE_BADGE[tone]
                )}
              >
                {t(paradigmBindingStateKey(binding.state))}
              </span>
            </>
          ) : (
            <span className="text-foreground/50 text-xs">{t('paradigmNotConnected')}</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {(actions.canConnect || actions.canChange) && (
            <IconAction
              label={actions.canChange ? t('paradigmChangeAccount') : t('paradigmConnectDesk')}
              icon={actions.canChange ? ArrowLeftRightIcon : PlugIcon}
              onClick={() => setShowSheet(true)}
            />
          )}
          {actions.canDisconnect && (
            <IconAction
              label={t('paradigmDisconnect')}
              icon={UnplugIcon}
              pending={isDisconnecting}
              onClick={() => setShowDisconnectDialog(true)}
            />
          )}
        </div>
      </div>

      {tone === 'mismatch' && (
        <div className="border-chart-red/20 bg-chart-red/5 flex w-full flex-col gap-2 border p-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangleIcon className="text-chart-red/70 mt-0.5 size-3.5 shrink-0" />
            <p className="text-chart-red/90 text-xs leading-relaxed">
              {t('paradigmMismatchWarning', { name: binding?.accountName ?? '' })}
            </p>
          </div>
          {binding?.mismatchDetectedAtMs && (
            <p className="text-foreground/50 text-xs">
              {t('paradigmMismatchDetected', {
                when: formatRelativeTime(binding.mismatchDetectedAtMs, tCommon),
              })}
            </p>
          )}
          {fixError && <p className="text-chart-red text-xs leading-relaxed">{fixError}</p>}
          {actions.canChange && (
            <div className="flex flex-wrap items-center gap-2">
              {suggested && (
                <Button
                  variant="basic"
                  type="button"
                  className="w-fit gap-1.5"
                  disabled={fixing}
                  onClick={() => void handleAutoFix()}
                >
                  {fixing && <Loader2Icon className="size-3 animate-spin" />}
                  {t('paradigmMismatchAutoFix', { name: suggested })}
                </Button>
              )}
              <Button
                variant="outline"
                type="button"
                className="w-fit"
                disabled={fixing}
                onClick={() => setShowSheet(true)}
              >
                {t('paradigmMismatchChoose')}
              </Button>
            </div>
          )}
        </div>
      )}

      {(actions.canConnect || actions.canChange) && (
        <ParadigmDeskSheet
          open={showSheet}
          onOpenChange={setShowSheet}
          accountId={account.accountId}
          // A binding exists only after a desk was registered, so the credential step
          // has nothing left to do — go straight to the nickname picker.
          skipCredentials={!!binding}
          highlightAccountName={suggested}
          onComplete={invalidateAccounts}
        />
      )}

      {/* Disconnect clears the desk id too, so this is not a nickname reset that one
          click undoes — the copy says so rather than implying otherwise. */}
      <DestructiveConfirmDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
        title={t('paradigmDisconnectTitle')}
        description={t('paradigmDisconnectDescription', { name: binding?.accountName ?? '' })}
        cancelLabel={tSettings('cancel')}
        confirmLabel={t('paradigmDisconnect')}
        pendingLabel={t('paradigmDisconnecting')}
        isPending={isDisconnecting}
        onConfirm={() => disconnectParadigm(account.accountId)}
      />
    </div>
  )
}
